import {useState} from 'react'
import {Link, NavLink, Outlet} from 'react-router-dom'
import {useAuth} from '../../modules/auth'
import {BRAND} from '../public/site'

const NAV = [
  {to: '/home', label: 'Home'},
  {to: '/payments', label: 'Payments'},
  {to: '/profile', label: 'Profile'},
]

/**
 * Renter chrome — mirrors the public marketing header (white sticky bar with the
 * logo) but, once signed in, the "Sign In" CTA is replaced by an avatar + name
 * button. Clicking it opens a right-hand drawer with the renter nav and sign-out.
 */
const RenterLayout = () => {
  const {currentUser} = useAuth()
  const [drawer, setDrawer] = useState(false)

  const name = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || 'Crew member'
  const initials =
    [currentUser?.first_name, currentUser?.last_name]
      .filter(Boolean)
      .map((n) => n![0]?.toUpperCase())
      .join('') || 'CC'

  return (
    <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f4f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif'}}>
      {/* Header */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'saturate(180%) blur(8px)',
          borderBottom: '1px solid #eef0f4',
        }}
      >
        <div style={{maxWidth: 1140, margin: '0 auto', padding: '0 20px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Link to='/home' style={{display: 'flex', alignItems: 'center', textDecoration: 'none'}}>
            <img src='/logo.png' alt='Crosscheck CrashPad' style={{height: 42, display: 'block'}} />
          </Link>

          {/* Desktop nav */}
          <nav className='d-none d-lg-flex' style={{alignItems: 'center', gap: 28}}>
            {NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                style={({isActive}) => ({
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  color: isActive ? BRAND.green : '#475569',
                })}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Avatar + name → opens the right drawer */}
          <button
            onClick={() => setDrawer(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 999,
              padding: '5px 14px 5px 5px',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${BRAND.green}, ${BRAND.greenDark})`,
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                letterSpacing: '0.02em',
              }}
            >
              {initials}
            </span>
            <span className='d-none d-sm-inline' style={{fontWeight: 600, fontSize: 14, color: BRAND.ink}}>{name}</span>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#94a3b8' strokeWidth='2.5' style={{flexShrink: 0}}>
              <polyline points='6 9 12 15 18 9' />
            </svg>
          </button>
        </div>
      </header>

      {/* Right drawer + backdrop */}
      {drawer && (
        <div
          onClick={() => setDrawer(false)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 100, animation: 'ccpFade 0.18s ease'}}
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              height: '100%',
              width: 320,
              maxWidth: '85vw',
              background: '#fff',
              boxShadow: '-12px 0 40px rgba(15,23,42,0.18)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'ccpSlideIn 0.22s ease',
            }}
          >
            {/* Drawer header — user identity */}
            <div style={{padding: '24px 22px', borderBottom: '1px solid #eef0f4', display: 'flex', alignItems: 'center', gap: 14}}>
              <span
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${BRAND.green}, ${BRAND.greenDark})`,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {initials}
              </span>
              <div style={{minWidth: 0}}>
                <div style={{fontWeight: 700, fontSize: 15, color: BRAND.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{name}</div>
                {currentUser?.email && (
                  <div style={{fontSize: 13, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{currentUser.email}</div>
                )}
              </div>
              <button
                onClick={() => setDrawer(false)}
                aria-label='Close'
                style={{marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: 22, lineHeight: 1, color: '#94a3b8', cursor: 'pointer'}}
              >
                ×
              </button>
            </div>

            {/* Drawer nav */}
            <nav style={{padding: '12px 12px', flex: 1}}>
              {NAV.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setDrawer(false)}
                  style={({isActive}) => ({
                    display: 'block',
                    padding: '12px 14px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: 15,
                    marginBottom: 4,
                    color: isActive ? BRAND.green : '#334155',
                    background: isActive ? '#f0faf4' : 'transparent',
                  })}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            {/* Sign out */}
            <div style={{padding: '16px 18px', borderTop: '1px solid #eef0f4'}}>
              <Link
                to='/logout'
                onClick={() => setDrawer(false)}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '11px 0',
                  borderRadius: 10,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                }}
              >
                Sign out
              </Link>
            </div>
          </aside>
        </div>
      )}

      <main style={{maxWidth: 960, margin: '0 auto', padding: '32px 20px', width: '100%', flex: 1, boxSizing: 'border-box'}}>
        <Outlet />
      </main>

      <style>{`
        @keyframes ccpFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ccpSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  )
}

export {RenterLayout}
